import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  Button,
  DialogActions,
  CircularProgress,
  Box,
  Autocomplete,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import AddCancelDialogButtons from "components/AddCancelDialogButtons";

/////////////Handles both Add and Edit modes based on riskMatrix prop
const AddRiskMatrixDialog = ({ open, onClose, riskMatrix, handleSubmit, handleDelete, compId }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [impact, setImpact] = useState("");
  const [likelihood, setLikelihood] = useState("");
  const [riskMitigation, setRiskMitigation] = useState("");
  const [riskOwner, setRiskOwner] = useState("");
  const [matrixName, setMatrixName] = useState("");
  const [errors, setErrors] = useState({});

  useEffect(() => {
    //////// Fill fields (so that their values are shown) for editing
    if (riskMatrix) {
      setImpact(
        riskMatrix.impact !== undefined && riskMatrix.impact !== null ? riskMatrix.impact : ""
      );
      setLikelihood(
        riskMatrix.likelihood !== undefined && riskMatrix.likelihood !== null
          ? riskMatrix.likelihood
          : ""
      );
      setRiskMitigation(riskMatrix.riskMitigation || "");
      setRiskOwner(riskMatrix.riskOwner || "");
      setMatrixName(riskMatrix.matrixName || "");
    }
    // Clear Fields for Adding
    else {
      setImpact("");
      setLikelihood("");
      setRiskMitigation("");
      setRiskOwner("");
      setMatrixName("");
    }
  }, [riskMatrix]);

  const validate = () => {
    let tempErrors = {};
    if (!matrixName)
      tempErrors.matrixName = t("admin.strategicInfo.riskManagement.dialog.validation.required");
    if (impact === "") {
      tempErrors.impact = t("admin.strategicInfo.riskManagement.dialog.validation.required");
    } else if (!Number.isInteger(Number(impact))) {
      tempErrors.impact = t(
        "admin.strategicInfo.riskManagement.dialog.validation.impactWholeNumber"
      );
    } else if (Number(impact) < 1 || Number(impact) > 3) {
      tempErrors.impact = t("admin.strategicInfo.riskManagement.dialog.validation.impactRange");
    }
    if (likelihood === "") {
      tempErrors.likelihood = t("admin.strategicInfo.riskManagement.dialog.validation.required");
    } else if (!Number.isInteger(Number(likelihood))) {
      tempErrors.likelihood = t(
        "admin.strategicInfo.riskManagement.dialog.validation.likelihoodWholeNumber"
      );
    } else if (Number(likelihood) < 1 || Number(likelihood) > 3) {
      tempErrors.likelihood = t(
        "admin.strategicInfo.riskManagement.dialog.validation.likelihoodRange"
      );
    }

    setErrors(tempErrors);
    return Object.keys(tempErrors).length === 0;
  };

  /////////////////////Submitting Data For Both Add and Edit Dialogs
  const handleSave = async () => {
    if (!validate()) return;
    setLoading(true);
    const isEditMode = !!riskMatrix;
    const addKey = !isEditMode; // a key to know whether the user is adding or editing

    const riskMatrixData = {
      ...riskMatrix, //used in edit mode (to concatenate the other fields)
      impact: impact,
      likelihood: likelihood,
      riskMitigation: riskMitigation,
      riskOwner: riskOwner,
      matrixName: matrixName,
      companyId: compId,
    };
    await handleSubmit(riskMatrixData, addKey); //It calls Add Risk Matrix In the other component
    setLoading(false);
    handleClose();
  };

  const handleClose = () => {
    setImpact("");
    setLikelihood("");
    setRiskMitigation("");
    setRiskOwner("");
    setErrors({});
    setMatrixName("");
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth>
      <DialogTitle>
        {riskMatrix
          ? t("admin.strategicInfo.riskManagement.dialog.editRiskMatrix") //shows edit if risk matrix is provided
          : t("admin.strategicInfo.riskManagement.dialog.addRiskMatrix")}
      </DialogTitle>
      {/* Same Form Fields for Add and Edit Dialog */}

      <DialogContent>
        <TextField
          label={t("admin.strategicInfo.riskManagement.dialog.matrixName") + " *"}
          value={matrixName}
          onChange={(e) => setMatrixName(e.target.value)}
          fullWidth
          margin="normal"
          error={!!errors.matrixName}
          helperText={errors.matrixName}
        />
        <TextField
          label={t("admin.strategicInfo.riskManagement.impact") + " *"}
          type="number"
          value={impact}
          onChange={(e) => setImpact(e.target.value)}
          fullWidth
          margin="normal"
          error={!!errors.impact}
          helperText={errors.impact}
          inputProps={{ min: 1, max: 3, step: 1 }}
        />
        <TextField
          label={t("admin.strategicInfo.riskManagement.likelihood") + " *"}
          type="number"
          value={likelihood}
          onChange={(e) => setLikelihood(e.target.value)}
          fullWidth
          margin="normal"
          error={!!errors.likelihood}
          helperText={errors.likelihood}
          inputProps={{ min: 1, max: 3, step: 1 }}
        />

        <TextField
          label={t("admin.strategicInfo.riskManagement.dialog.riskOwner")}
          value={riskOwner}
          onChange={(e) => setRiskOwner(e.target.value)}
          fullWidth
          margin="normal"
        />

        <TextField
          label={t("admin.strategicInfo.riskManagement.dialog.riskMitigation")}
          value={riskMitigation}
          onChange={(e) => setRiskMitigation(e.target.value)}
          fullWidth
          margin="normal"
        />
      </DialogContent>

      <AddCancelDialogButtons
        onCancel={handleClose}
        onSubmit={handleSave}
        loading={loading}
        cancelLabel={t("admin.strategicInfo.initiatives.dialog.cancel")}
        submitLabel={t("admin.strategicInfo.initiatives.dialog.add")}
      />

      {loading && (
        <Box display="flex" justifyContent="center" p={2}>
          <CircularProgress size={20} />
        </Box>
      )}
    </Dialog>
  );
};

AddRiskMatrixDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  riskMatrix: PropTypes.object,
  handleSubmit: PropTypes.func.isRequired,
  handleDelete: PropTypes.func.isRequired,
  compId: PropTypes.number.isRequired,
};

export default AddRiskMatrixDialog;
